import React, { FC, useContext, useState } from 'react';
import { GridLegacy as Grid } from "@mui/material";

import { IPreferences, } from "../../logic/api";

import { IDropDownListItem } from "../../components/editable/EditableField/fields/DropDownField";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import Editable from '../../components/editable/EditableField/components/Editable';

import { SessionContext } from "../../utility/useSession";
import { isSysAdmin } from "../../utility/utils";

import styles from './styles';
import { useTranslation } from 'react-i18next';
import { ReleaseFlag, useReleaseManagement } from '../../utility/feature-management';

interface IProvider extends IDropDownListItem {
  value: 'parkspace' | 'parkit' | '';
}

const parkspaceProvider: IProvider = {
  name: 'Chauntry',
  value: 'parkspace',
};

const parkitProvider: IProvider = {
  name: 'ParkIT',
  value: 'parkit',
};

const notConfiguredProvider: IProvider = {
  name: 'None',
  value: 'parkit',
}

const providers: IProvider[] = [
  parkitProvider,
  parkspaceProvider,
];

function toDropDownListItem(setting: string): IDropDownListItem {
  if (!setting || typeof setting !== 'string') {
    return notConfiguredProvider;
  }

  switch (setting) {
    case parkspaceProvider.value:
      return parkspaceProvider

    case parkitProvider.value:
      return parkitProvider

    default:
      return notConfiguredProvider
  }
}

const ChauntryConfigFields: FC = () => {
  const [password, setPassword] = useState<string>('');
  const [providerConfirmationFallback, setProviderConfirmationFallback] = useState<boolean>(true);
  const { t } = useTranslation();
  const useParkspaceRatesReleased = useReleaseManagement(ReleaseFlag.releaseUseParkspaceRates);


  return (
    <Grid container>
      {useParkspaceRatesReleased && <><EditableField<IPreferences, boolean>
        useFullWidthWhenEditing={false}
        inputType={EditableFieldTypes.switch}
        label={t("settings.productCatalogueProviderConfirmation")}
        onProvide={(data) => data.product_catalogue_provider_config?.config?.provider_confirmation || data.product_catalogue_provider_config?.config?.provider_confirmation === false ? Boolean(data.product_catalogue_provider_config?.config?.provider_confirmation) : providerConfirmationFallback}
        onChange={(data, value) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }

          setProviderConfirmationFallback(Boolean(value))
          data.product_catalogue_provider_config.config.provider_confirmation = Boolean(value);
        }
        }
      />

        <EditableField<IPreferences, string>
          inputType={EditableFieldTypes.empty}
          label={""}
        /></>}

      <EditableField<IPreferences, string>
        inputType={EditableFieldTypes.text}
        label={t("settings.username")}
        onProvide={(data) => data.product_catalogue_provider_config?.config?.username as string || ""}
        onChange={(data, username) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }

          data.product_catalogue_provider_config.config.username = username;
        }}
      />
      <EditableField<IPreferences, string>
        inputType={EditableFieldTypes.password}
        label={t("login.intro.passwordFieldLabel")}
        onProvide={() => password}
        onChange={(data, value) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }
          data.product_catalogue_provider_config.config.password = value;

          setPassword(value)
        }}
      />
      <EditableField<IPreferences, string>
        inputType={EditableFieldTypes.text}
        label={t("settings.customerCode")}
        onProvide={(data) => data.product_catalogue_provider_config?.config?.customerCode as string || ""}
        onChange={(data, customerCode) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }

          data.product_catalogue_provider_config.config.customerCode = customerCode;
        }}
      />
      <EditableField<IPreferences, string>
        inputType={EditableFieldTypes.text}
        label={t("settings.carparkCode")}
        maxLength={8}
        onProvide={(data) => data.product_catalogue_provider_config?.config?.carParkCode as string || ""}
        onChange={(data, carParkCode) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }

          data.product_catalogue_provider_config.config.carParkCode = carParkCode;
        }}
      />
      <EditableField<IPreferences, string>
        inputType={EditableFieldTypes.text}
        label={t("settings.walkInAgentCode")}
        onProvide={(data) => data.product_catalogue_provider_config?.config?.agentCode as string || ""}
        onChange={(data, agentCode) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }

          data.product_catalogue_provider_config.config.agentCode = agentCode;
        }}
      />
      <EditableField<IPreferences, string>
        inputType={EditableFieldTypes.text}
        label="Chauntry URI"
        onProvide={(data) => data.product_catalogue_provider_config?.config?.uri as string || ""}
        onChange={(data, uri) => {
          if (!data.product_catalogue_provider_config) {
            data.product_catalogue_provider_config = {
              type: parkspaceProvider.value,
              config: {}
            }
          }

          data.product_catalogue_provider_config.config.uri = uri;
        }}
      />
    </Grid>
  )
}

interface IProductCatalogueSettingProps {
  onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const ProductCatalogueSetting: FC<IProductCatalogueSettingProps> = ({ onSave }) => {
  const session = useContext(SessionContext);
  const currentProvider = session.current.site?.preferences.product_catalogue_provider_config?.type;
  const [selectedProvider, setSelectedProvider] = useState<string | undefined>(currentProvider);
  const { t } = useTranslation();

  if (!isSysAdmin(session) || !session.current.site) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <NotchedCard label={t("settings.productCatalogue")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="productCatalogueSettings"
          editType={EditableGroupType.switchableEditing}
          data={session.current.site.preferences}
          onDataChanged={(data) => onSave({
            product_catalogue_provider_config: data.product_catalogue_provider_config,
            product_catalogue_provider_packages: data.product_catalogue_provider_packages,
            product_catalogue_provider_overstays: data.product_catalogue_provider_overstays,
            product_catalogue_provider_products: data.product_catalogue_provider_products,
          })}
          testId="product-catalogue"
        >
          <Editable>
            <p style={styles.product_catalogue_subheading}>{t("settings.configProvider")}</p>
          </Editable>
          <EditableField<IPreferences, IDropDownListItem>
            inputType={EditableFieldTypes.terminalField}
            label={t("settings.dynamicProvider")}
            onProvide={(data) => {
              if (!data.product_catalogue_provider_config) {
                return notConfiguredProvider
              }

              switch (data.product_catalogue_provider_config.type) {
                case parkspaceProvider.value:
                  return parkspaceProvider

                case parkitProvider.value:
                  return notConfiguredProvider // The requirement is that the UI is unaware that a "not configured" dynamic provider is _really_ parkit.

                default:
                  return notConfiguredProvider
              }
            }}
            onRequestData={() => Promise.resolve([notConfiguredProvider, parkspaceProvider])}
            onChange={(data, value) => {
              data.product_catalogue_provider_config = {
                type: value.value || "",
                config: {},
              }

              setSelectedProvider(value.value)
            }}
            testId="settings-product-catalogue-provider-config-field"
          />
          {
            selectedProvider === parkspaceProvider.value && (
              <ChauntryConfigFields />
            )
          }
          <Editable>
            <p style={styles.product_catalogue_subheading}>{t("settings.setCatalogue")}</p>
          </Editable>
          <Grid container>
            <EditableField<IPreferences, IDropDownListItem>
              inputType={EditableFieldTypes.terminalField}
              label={t("kmos.labels.packages")}
              onProvide={(data) => toDropDownListItem(data.product_catalogue_provider_packages)}
              onRequestData={() => Promise.resolve(providers)}
              onChange={(data, value) => {
                data.product_catalogue_provider_packages = value.value || ""
              }}
              testId="settings-product-catalogue-provider-packages-field"
            />
            <EditableField<IPreferences, IDropDownListItem>
              inputType={EditableFieldTypes.terminalField}
              label={t("settings.overstays")}
              onProvide={(data) => toDropDownListItem(data.product_catalogue_provider_overstays)}
              onRequestData={() => Promise.resolve(providers)}
              onChange={(data, value) => {
                data.product_catalogue_provider_overstays = value.value || ""
              }}
              testId="settings-product-catalogue-provider-overstays-field"
            />
            <EditableField<IPreferences, IDropDownListItem>
              inputType={EditableFieldTypes.terminalField}
              label={t("settings.productsAddOns")}
              onProvide={(data) => toDropDownListItem(data.product_catalogue_provider_products)}
              onRequestData={() => Promise.resolve(providers)}
              onChange={(data, value) => {
                data.product_catalogue_provider_products = value.value || ""
              }}
              testId="settings-product-catalogue-provider-products-field"
            />
          </Grid>
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
}
